import { useEffect, useState } from "react";
import { AcuantCameraStateTypes } from "@enums/acuant-camera-state-types";
import {
    AcuantCameraCaptureCallback,
    AcuantCameraHTMLCanvasElement,
    AcuantCameraOnDetectedResult,
    AcuantCameraOptions
} from "@idw-types/acuant-camera-ui";
import {
    drawOverlay,
    drawPermanentOverlay,
} from "@utils/acuant-camera-canvas-util";
import { CaptureStepTypes } from "@enums/capture-step-types";

type CustomAcuantCameraUIProps = {
    captureStepType: CaptureStepTypes;
    options: AcuantCameraOptions;
    captureCallback: AcuantCameraCaptureCallback;
    onLiveCaptureError: (error: string, code: number) => void;
    onLiveCaptureStateChange: (newText: string) => void;
};

type CustomAcuantCameraUIState = {
    isStarted: boolean;
    onDetectedResult: AcuantCameraOnDetectedResult;
};

export const CustomAcuantCameraUI = (props: CustomAcuantCameraUIProps) => {
    const {
        captureStepType,
        options,
        captureCallback,
        onLiveCaptureError,
        onLiveCaptureStateChange
    } = props;

    const DETECT_TIME_THRESHOLD = 400;
    let minTime = Number.MAX_VALUE;

    let countdownStartTime: number = null;
    let timeout: NodeJS.Timeout = null;
    let uiContext: CanvasRenderingContext2D = null;
    let acuantCamera: HTMLElement = null;
    let uiCanvas: AcuantCameraHTMLCanvasElement = null;

    const [state, setState] = useState<CustomAcuantCameraUIState>({
        isStarted: false,
        onDetectedResult: null
    });

    useEffect(() => {
        if (
            state.onDetectedResult?.state === AcuantCameraStateTypes.TAP_TO_CAPTURE &&
            uiCanvas &&
            uiContext
        ) {
            onLiveCaptureStateChange(options.text.TAP_TO_CAPTURE);
        }
    }, [state.onDetectedResult?.state, uiCanvas, uiContext]);

    const reset = () => {
        setState({
            ...state,
            onDetectedResult: null,
        });
        countdownStartTime = null;
    };

    const handleUi = (onDetectedResult: AcuantCameraOnDetectedResult) => {
        if (uiContext && uiCanvas) {
            uiContext.clearRect(0, 0, uiCanvas.width, uiCanvas.height);
            drawPermanentOverlay(uiCanvas, uiContext, "rgba(173, 216, 230, 0.4)", captureStepType);

            if (!onDetectedResult) {
                onLiveCaptureStateChange(options.text.NONE);
            } else if (onDetectedResult.state === AcuantCameraStateTypes.CAPTURING) {
                drawOverlay(uiContext, onDetectedResult, 'rgba(255, 255, 0, 0.2)');
            } else if (onDetectedResult.state === AcuantCameraStateTypes.TAP_TO_CAPTURE) {
                onLiveCaptureStateChange(options.text.TAP_TO_CAPTURE);
            } else if (onDetectedResult.state === AcuantCamera.DOCUMENT_STATE.GOOD_DOCUMENT) {
                drawOverlay(uiContext, onDetectedResult, 'rgba(255, 255, 0, 0.2)');

                if (options.text.GOOD_DOCUMENT) {
                    onLiveCaptureStateChange(options.text.GOOD_DOCUMENT);
                } else if (countdownStartTime !== null) {
                    const remainder = (options.triggerTime - (new Date().getTime() - countdownStartTime)) / 1000;
                    const numSeconds = Math.ceil(remainder) || 1;
                    onLiveCaptureStateChange(`${numSeconds}...`);
                } else {
                    const numSeconds = Math.ceil(options.triggerTime / 1000);
                    countdownStartTime = new Date().getTime();
                    onLiveCaptureStateChange(`${numSeconds}...`);
                }             
            } else {
                onLiveCaptureStateChange(options.text.NONE);                
            }
        }
    };

    const onAcuantCameraCreated = () => {
        uiCanvas = document.getElementById('acuant-ui-canvas') as AcuantCameraHTMLCanvasElement;

        if (uiCanvas) {
            uiCanvas.setAttribute('role', 'img');
            uiContext = uiCanvas.getContext('2d');
        }
    };

    const cancelCountDown = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }
        countdownStartTime = null;
    };

    const triggerCountDown = () => {
        if (timeout === null) {
            timeout = setTimeout(function () {
                setState({
                    ...state,
                    onDetectedResult: {
                        ...(state.onDetectedResult || {}),
                        state: AcuantCameraStateTypes.CAPTURING
                    }
                });
                countdownStartTime = null;
                capture(captureCallback, 'AUTO');
            }, options.triggerTime);
        }
    };

    const handleLiveCapture = (response: AcuantCameraOnDetectedResult) => {
        if (response) {
            handleUi(response);
            if (state.onDetectedResult && state.onDetectedResult.state === AcuantCameraStateTypes.CAPTURING) {
                return;
            }

            if (captureCallback.onFrameAvailable) {
                captureCallback.onFrameAvailable(response);
            }

            if (response.state === AcuantCamera.DOCUMENT_STATE.GOOD_DOCUMENT) {
                if (timeout === null) {
                    setState({ ...state, onDetectedResult: response });
                    triggerCountDown();
                }
            } else {
                cancelCountDown();
            }
        } else {
            cancelCountDown();
        }
    };

    const handleTime = (start: number, count: number) => {
        if (count >= 3) {
            return true;
        } else {
            const elapsed = new Date().getTime() - start;

            if (elapsed < minTime) {
                minTime = elapsed;
            }
            return minTime < DETECT_TIME_THRESHOLD;
        }
    };

    const capture = (captureCb: AcuantCameraCaptureCallback, capType: string) => {
        AcuantCamera.triggerCapture((response) => {
            end();
            if (document.fullscreenElement) {
                document.exitFullscreen().then(() => {
                    captureCb.onCaptured(response);
                });
            }
            else {
                captureCb.onCaptured(response);
            }

            AcuantCamera.evaluateImage(response.data, response.width, response.height, response.isPortraitOrientation, capType, (result) => {
                captureCb.onCropped(result);
            });
        });
    };

    const startTapToCapture = () => {
        setState({
            ...state,
            onDetectedResult: {
                state: AcuantCameraStateTypes.TAP_TO_CAPTURE
            }
        });
        setTimeout(() => {
            handleUi({ state: AcuantCameraStateTypes.TAP_TO_CAPTURE });
        }, 100);
    };

    const onError = (error: string, code: number) => {
        end();
        if (onLiveCaptureError) {
            onLiveCaptureError(error, code);
        }
    };

    const startCamera = () => {
        let count = 0;
        let startTime = new Date().getTime();

        acuantCamera = document.getElementById('acuant-camera');
        if (acuantCamera) {
            acuantCamera.addEventListener('acuantcameracreated', onAcuantCameraCreated);
        }

        AcuantCamera.start((response) => {
            if (handleTime(startTime, count)) {
                if (minTime < DETECT_TIME_THRESHOLD) {
                    handleLiveCapture(response);
                } else {
                    startTapToCapture();
                }
            } else {
                count += 1;
                startTime = new Date().getTime();
                AcuantCamera.setRepeatFrameProcessor();
            }
        }, captureCallback, onError);
    };

    const start = () => {
        if (AcuantCamera.isCameraSupported) {
            if (!state.isStarted) {
                setState({ ...state, isStarted: true });
                reset();
                startCamera();
            }
        } else {
            onError('Camera not supported.', AcuantJavascriptWebSdk.START_FAIL_CODE);
        }
    };

    const end = () => {
        reset();
        setState({ ...state, isStarted: false });
        if (timeout) {
            clearTimeout(timeout);
            timeout = null;
        }

        if (acuantCamera) {
            acuantCamera.removeEventListener('acuantcameracreated', onAcuantCameraCreated);
        }
        if (uiCanvas) {
            uiCanvas.getContext('2d').clearRect(0, 0, uiCanvas.width, uiCanvas.height);
        }
        AcuantCamera.end();
    };

    return {
        start,
        end,
        capture
    };
};